
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Transaction } from "@/components/wallet/TransactionHistory";
import { sanitizeInput } from "@/utils/security";

export const useWallet = () => {
  const { toast } = useToast();
  const [isConnected, setIsConnected] = useState(false);
  const [balance, setBalance] = useState("0.00");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  
  // Mock wallet address - in a real implementation this would come from Web3 connection
  const walletAddress = "0x1234...5678";

  // Initialize with mock transactions - in real implementation, these would come from blockchain
  useEffect(() => {
    if (isConnected) {
      setTransactions([
        {
          id: 1,
          type: "deposit",
          amount: "0.5 ETH",
          status: "completed",
          date: "2024-03-20T14:30:00",
          hash: "0xabcd1234efgh5678ijkl9012mnop3456",
          verified: true
        },
        {
          id: 2,
          type: "withdrawal",
          amount: "0.2 ETH",
          status: "pending",
          date: "2024-03-19T09:15:00",
          hash: "0xefgh5678ijkl9012mnop3456qrst7890"
        },
        {
          id: 3,
          type: "deposit",
          amount: "1.0 ETH",
          status: "completed",
          date: "2024-03-18T16:45:00",
          hash: "0xijkl9012mnop3456qrst7890uvwx1234",
          verified: true
        }
      ]);
    }
  }, [isConnected]);

  const handleConnect = useCallback(() => {
    setIsConnected(true);
    toast({
      title: "Wallet Connected",
      description: "Your wallet has been successfully connected.",
      variant: "success"
    });
  }, [toast]);

  const handleDeposit = useCallback((amount: string) => {
    if (!amount) return;
    
    // Sanitize and validate input
    const sanitizedAmount = sanitizeInput(amount);
    const depositAmount = parseFloat(sanitizedAmount);
    
    if (isNaN(depositAmount) || depositAmount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid positive amount.",
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Deposit Initiated",
      description: `Depositing ${depositAmount.toFixed(2)} ETH to your account.`,
      variant: "success"
    });
    
    // Simulate balance update
    setBalance((prev) => (Number(prev) + depositAmount).toFixed(2));
    
    // Add transaction to history
    const newTransaction: Transaction = {
      id: Date.now(),
      type: "deposit",
      amount: `${depositAmount.toFixed(2)} ETH`,
      status: "pending",
      date: new Date().toISOString(),
      hash: `0x${Math.random().toString(16).substring(2, 14)}${Math.random().toString(16).substring(2, 14)}`
    };
    
    setTransactions(prev => [newTransaction, ...prev]);
    
    // Simulate transaction completion after 2 seconds
    setTimeout(() => {
      setTransactions(prev => 
        prev.map(tx => 
          tx.id === newTransaction.id 
            ? { ...tx, status: "completed", verified: true } 
            : tx
        )
      );
      
      toast({
        title: "Deposit Completed",
        description: `${depositAmount.toFixed(2)} ETH has been added to your account.`,
        variant: "success"
      });
    }, 2000);
    
  }, [toast]);

  const handleWithdraw = useCallback((amount: string) => {
    if (!amount) return;
    
    // Sanitize and validate input
    const sanitizedAmount = sanitizeInput(amount);
    const withdrawAmount = parseFloat(sanitizedAmount);
    
    if (isNaN(withdrawAmount) || withdrawAmount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid positive amount.",
        variant: "destructive"
      });
      return;
    }
    
    if (withdrawAmount > Number(balance)) {
      toast({
        title: "Withdrawal Failed",
        description: "Insufficient balance for withdrawal.",
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Withdrawal Initiated",
      description: `Withdrawing ${withdrawAmount.toFixed(2)} ETH from your account.`,
      variant: "success"
    });
    
    // Simulate balance update
    setBalance((prev) => (Number(prev) - withdrawAmount).toFixed(2));
    
    // Add transaction to history
    const newTransaction: Transaction = {
      id: Date.now(),
      type: "withdrawal",
      amount: `${withdrawAmount.toFixed(2)} ETH`,
      status: "pending",
      date: new Date().toISOString(),
      hash: `0x${Math.random().toString(16).substring(2, 14)}${Math.random().toString(16).substring(2, 14)}`
    };
    
    setTransactions(prev => [newTransaction, ...prev]);
    
    // Simulate transaction completion after 2 seconds
    setTimeout(() => {
      setTransactions(prev => 
        prev.map(tx => 
          tx.id === newTransaction.id 
            ? { ...tx, status: "completed", verified: true } 
            : tx
        )
      );
      
      toast({
        title: "Withdrawal Completed",
        description: `${withdrawAmount.toFixed(2)} ETH has been withdrawn from your account.`,
        variant: "success"
      });
    }, 2000);
    
  }, [toast, balance]);

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    
    // Simulate refresh delay
    setTimeout(() => {
      setIsRefreshing(false);
      toast({
        title: "Balance Updated",
        description: "Your wallet balance has been refreshed.",
        variant: "success"
      });
    }, 1000);
  }, [toast]);

  return {
    isConnected,
    balance,
    walletAddress,
    isRefreshing,
    transactions,
    handleConnect,
    handleDeposit,
    handleWithdraw,
    handleRefresh
  };
};
